/**
 * Business_rule_canvas branch — client-side fallback layout for RuleFlowCanvas.
 *
 * PR #635 review (C1, Critical): every real node from BE/ORCH arrives with `position: null` —
 * confirmed directly in shay-email-ml#603's parse/serialize code and test fixtures, with the
 * field itself commented "Canvas-only layout hint (React Flow). Round-tripped, never
 * interpreted." No producer ever sets a real {x, y}. React Flow's Node.position is required and
 * used directly in its internal transform math, so handing it null/undefined either throws
 * during render or collapses every node onto the same coordinate.
 *
 * A standalone layered longest-path layout (same class of algorithm as
 * WorkflowCanvasTab.tsx's own computeAutoLayout, deliberately reimplemented here rather than
 * imported from it — this feature stays fully independent of the legacy builder, matching the
 * ADR/TDD's own "never touches the deterministic workflow" boundary already applied to
 * RuleNode.tsx vs PrimitiveNode.tsx).
 */
export interface FallbackLayoutEdge {
  source: string;
  target: string;
}

/** True only for a genuine, already-usable {x, y} pair — anything else (null, undefined, a
 * non-numeric x/y) is treated as "needs a fallback position", not trusted as-is. */
export function hasValidPosition(position: unknown): position is { x: number; y: number } {
  return (
    !!position &&
    typeof position === 'object' &&
    typeof (position as { x?: unknown }).x === 'number' &&
    typeof (position as { y?: unknown }).y === 'number'
  );
}

// LAYER_GAP clears a node card's height (~90-150px); SIBLING_GAP clears its width (~180-260px,
// RuleNode.tsx's minWidth/maxWidth). Which axis (x or y) each one applies to must follow the
// card's actual dimension in that direction, not just "whichever counter is currently primary" —
// swapping x/y naively without also keeping each gap pinned to its own axis (see below) is what
// caused horizontal-mode nodes to overlap: it put the height-sized gap between side-by-side
// cards, which are wider than that.
const LAYER_GAP = 160;
const SIBLING_GAP = 220;
// SIBLING_GAP (220) is already narrower than RuleNode.tsx's own maxWidth (260) — fine for
// vertical mode's sibling axis (parallel branches, rarely both at max width at once), but in
// horizontal mode this same axis carries the primary chain of full-width cards one after
// another, so 220 still left them touching/overlapping even after the earlier straight-line and
// overlap fixes. A dedicated, more generous gap for horizontal mode's chain axis only — doesn't
// touch SIBLING_GAP's existing vertical-mode behavior at all.
const HORIZONTAL_CHAIN_GAP = 340;

/**
 * Layered longest-path relaxation: nodes with no incoming edge start at layer 0, everything
 * else is pushed to max(source layer) + 1, bounded by node count so a cycle can't loop forever.
 * Good enough for these flows' typical size (a handful of TRIGGER/ACTION/CONDITION nodes) —
 * not meant to compete with a real dagre/elkjs layout, just to guarantee every node lands on a
 * distinct, readable position instead of null/NaN or full overlap.
 */
export function computeFallbackPositions(
  nodeIds: string[],
  edges: FallbackLayoutEdge[],
  // Defaults to 'vertical' so every existing caller (the initial-load fallback for nodes
  // BE/ORCH sends with position: null) is unaffected — only the Auto-layout button passes
  // 'horizontal' explicitly, alternating each click.
  direction: 'vertical' | 'horizontal' = 'vertical'
): Record<string, { x: number; y: number }> {
  const layerByNodeId: Record<string, number> = {};
  nodeIds.forEach((id) => {
    layerByNodeId[id] = 0;
  });

  for (let pass = 0; pass < nodeIds.length; pass += 1) {
    let changed = false;
    edges.forEach((e) => {
      if (layerByNodeId[e.source] === undefined || layerByNodeId[e.target] === undefined) return;
      if (layerByNodeId[e.target] < layerByNodeId[e.source] + 1) {
        layerByNodeId[e.target] = layerByNodeId[e.source] + 1;
        changed = true;
      }
    });
    if (!changed) break;
  }

  const idsByLayer: Record<number, string[]> = {};
  nodeIds.forEach((id) => {
    const layer = layerByNodeId[id] ?? 0;
    (idsByLayer[layer] ??= []).push(id);
  });

  const positions: Record<string, { x: number; y: number }> = {};
  Object.keys(idsByLayer)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((layer) => {
      idsByLayer[layer].forEach((id, i) => {
        // y always advances by LAYER_GAP (height-sized), regardless of direction. x advances by
        // SIBLING_GAP for vertical mode's sibling axis (unchanged), or HORIZONTAL_CHAIN_GAP for
        // horizontal mode's chain axis (wider — see comment above).
        positions[id] =
          direction === 'horizontal'
            ? { x: layer * HORIZONTAL_CHAIN_GAP, y: i * LAYER_GAP }
            : { x: i * SIBLING_GAP, y: layer * LAYER_GAP };
      });
    });
  return positions;
}
